var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { DexfSettingsUtility, PriceUtility, TotalPriceCalculator } from '@inter-ikea-kompis/utilities';
import { EcoFee } from '@inter-ikea-kompis/component-eco-fee';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { Text } from '@inter-ikea-kompis/component-text';
import { CommercialMessage as SkapaCommercialMessage } from '@ingka/commercial-message-webc/passive.js';
import MiniPriceAlignmentEnum from '../../enums/MiniPriceAlignmentEnum.js';
import MiniPriceModalEnum from '../../enums/MiniPriceModalEnum.js';
import MiniPriceStyle from './MiniPriceStyle.js';
/**
 * This price layout is typically used in the Mini Configuration Summary and the Planning Header in the planning step for total prices. It has reduced information, e.g. not supporting all commercial messages or compliance requirements.
 *
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.modalType tells you the type
 */
export default class MiniPrice extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Set price to be animated
        this.animatedPrice = false;
        // Align slot
        this.align = null;
        // Show eco fee
        this.showEcoFee = false;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Show a modal.
        this.visibleModal = null;
        this.dexfSettings = null;
    }
    /**
     * Renders view.
     *
     * @returns Template.
     */
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        if (!this.shoppingProducts) {
            return null;
        }
        const totalPrice = TotalPriceCalculator.calculateMultiple(this.shoppingProducts, this.getSettings().localisation.dateFormat);
        const primaryCurrency = totalPrice[0];
        return html `
			<style>
				${MiniPriceStyle.getStyle({
            theme: this.theme,
            align: this.align,
            hasDualCurrencies: totalPrice.length > 1,
            isTRO: PriceUtility.isTro(primaryCurrency)
        })}
			</style>

			${this.getCommercialMessage(primaryCurrency)}

			<div class="priceContainer">
				${this.align === MiniPriceAlignmentEnum.right
            ? html `
							<slot></slot>
							<div class="divider"></div>
						`
            : ''}

				<div class="innerPriceContainer">
					${totalPrice.map((price, index) => {
            const isPrimaryCurrency = index < 1;
            return html `
							${this.getRegularPrice(price)} ${this.getPrice(price, isPrimaryCurrency)}
						`;
        })}
				</div>

				${this.align !== MiniPriceAlignmentEnum.right
            ? html `
							<slot></slot>
							<div class="divider"></div>
						`
            : ''}
			</div>

			${this.getText(primaryCurrency)} ${this.getEcoFee(primaryCurrency)}
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-commercial-message', SkapaCommercialMessage);
        this.customElements.define('kompis-price-formatter', PriceFormatter);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-eco-fee', EcoFee);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    /**
     * Returns eco fee.
     *
     * @param totalPrice Price model.
     * @returns Template.
     */
    getEcoFee(totalPrice) {
        if (!this.showEcoFee || (!totalPrice.ecoPrice && !totalPrice.weeePrice)) {
            return null;
        }
        return html `
			<kompis-eco-fee
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.theme="${this.theme}"
				.totalPrice="${totalPrice}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal ? this.visibleModal : null}"
				@modalClose="${(event) => {
            this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }));
            event.detail?.triggerElement?.focus();
        }}"
				@modalOpen="${() => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: { visibleModal: MiniPriceModalEnum.ecoFeeCard }
        }))}"
			></kompis-eco-fee>
		`;
    }
    /**
     * Get the commercial message.
     *
     * @param totalPrice Price model.
     * @returns Commercial message.
     */
    getCommercialMessage(totalPrice) {
        let variant;
        if (PriceUtility.isFamily(totalPrice)) {
            variant = 'family';
        }
        if (!variant) {
            return null;
        }
        return html `
			<skapa-commercial-message variant="${variant}" subtle>
				${this.getTranslations().priceInfoIkeaFamilyPrice}
			</skapa-commercial-message>
		`;
    }
    /**
     * Get the regular price.
     *
     * @param totalPrice Price model.
     * @returns Regular price.
     */
    getRegularPrice(totalPrice) {
        if (!PriceUtility.isTro(totalPrice)) {
            return null;
        }
        return html `
			<kompis-text
				.theme="${this.theme}"
				.type="${ThemeFontStyleTypeEnum.priceXSmall}"
				.bold="${true}"
			>
				<kompis-price-formatter
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.amount="${totalPrice.regularInclTax}"
					.strikeThrough="${true}"
					.ariaLabelPrice="${this.getTranslations().priceInfoRegularPrice}"
					.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
				>
				</kompis-price-formatter>
			</kompis-text>
		`;
    }
    /**
     * Get the current price.
     *
     * @param totalPrice Price model.
     * @param isPrimaryCurrency ...
     * @returns Current price.
     */
    getPrice(totalPrice, isPrimaryCurrency) {
        return html `
			<kompis-text
				class="price"
				.theme="${this.theme}"
				.type="${this.getPriceFontStyleType(totalPrice, isPrimaryCurrency)}"
				.bold="${true}"
			>
				<kompis-price-formatter
					.localizedInformation="${this.localizedInformation}"
					.settings="${this.settings}"
					.animated="${this.animatedPrice}"
					.amount="${PriceUtility.getPrice(totalPrice)}"
					.mixedSize="${this.getPriceMixedSize(totalPrice, isPrimaryCurrency)}"
					.ariaLabelPrice="${this.getTranslations().priceInfoCurrentPrice}"
					.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
				>
				</kompis-price-formatter>
			</kompis-text>
		`;
    }
    /**
     * Get the text.
     *
     * @param totalPrice Price model.
     * @returns The text.
     */
    getText(totalPrice) {
        if (!PriceUtility.isTroOrFamily(totalPrice)) {
            return null;
        }
        if (PriceUtility.isFamily(totalPrice)) {
            return html `${this.getRegularPriceText(totalPrice)} ${this.getValidTimePeriod(totalPrice)}`;
        }
        if (PriceUtility.isTro(totalPrice)) {
            return html `${this.getValidTimePeriod(totalPrice)}`;
        }
        return null;
    }
    /**
     * Get correct translation for time period.
     *
     * @param totalPrice Price model.
     * @returns Translated price period.
     */
    getValidTimePeriod(totalPrice) {
        const settings = this.getSettings();
        const updatedSettings = {
            ...settings,
            kompis: {
                ...settings.kompis,
                priceSettings: {
                    ...settings.kompis.priceSettings,
                    temporaryPricePeriod: settings.kompis.priceSettings.temporaryPricePeriodMiniSummary
                }
            }
        };
        const validInterval = PriceUtility.getValidInterval(totalPrice, undefined, this.getTranslations(), updatedSettings);
        return validInterval
            ? html `
					<kompis-text
						class="text"
						.theme="${this.theme}"
						.type="${ThemeFontStyleTypeEnum.bodySmall}"
					>
						${validInterval}
					</kompis-text>
				`
            : null;
    }
    /**
     * Get regular price text.
     * If loyaltyProgramEqualFontSize is true, it will render the same amount, but with enlarged font size.
     *
     * @param totalPrice Price model.
     * @returns Regular price text.
     */
    getRegularPriceText(totalPrice) {
        const loyaltyProgramEqualFontSize = this.getSettings().kompis.priceSettings.loyaltyProgramEqualFontSize;
        const priceInfoRegularPrice = this.getTranslations().priceInfoRegularPrice;
        if (loyaltyProgramEqualFontSize) {
            return html `
				<kompis-text
					aria-hidden="true"
					.theme="${this.theme}"
					.bold="${true}"
					.type="${ThemeFontStyleTypeEnum.bodySmall}"
				>
					${priceInfoRegularPrice}
				</kompis-text>

				<kompis-text
					class="price"
					.theme="${this.theme}"
					.bold="${true}"
					.type="${this.getPriceFontStyleType(totalPrice, true)}"
				>
					<kompis-price-formatter
						.localizedInformation="${this.localizedInformation}"
						.settings="${this.settings}"
						.animated="${this.animatedPrice}"
						.amount="${totalPrice.regularInclTax}"
						.mixedSize="${this.getPriceMixedSize(totalPrice, true)}"
						.ariaLabelPrice="${priceInfoRegularPrice}"
						.currencySymbol="${PriceUtility.getCurrencySymbol(totalPrice, this.getSettings())}"
					>
					</kompis-price-formatter>
				</kompis-text>
			`;
        }
        return html `
			<kompis-text class="text" .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
				<div class="regularPrice">
					<span aria-hidden="true">${priceInfoRegularPrice}:</span>
					<div class="regularPriceTextDivider"></div>

					<kompis-price-formatter
						.localizedInformation="${this.localizedInformation}"
						.settings="${this.settings}"
						.amount="${totalPrice.regularInclTax}"
						.ariaLabelPrice="${priceInfoRegularPrice}"
					>
					</kompis-price-formatter>
				</div>
			</kompis-text>
		`;
    }
    /**
     * Price font style type.
     *
     * @param totalPrice Price model.
     * @param isPrimaryCurrency ...
     * @returns Font style type.
     */
    getPriceFontStyleType(totalPrice, isPrimaryCurrency) {
        if (PriceUtility.isFamily(totalPrice)) {
            return isPrimaryCurrency
                ? ThemeFontStyleTypeEnum.priceMedium
                : ThemeFontStyleTypeEnum.priceXSmall;
        }
        if (PriceUtility.isTro(totalPrice)) {
            return ThemeFontStyleTypeEnum.priceMedium;
        }
        return isPrimaryCurrency
            ? ThemeFontStyleTypeEnum.priceXLarge
            : ThemeFontStyleTypeEnum.priceMedium;
    }
    /**
     * Returns if price is mixed size.
     *
     * @param totalPrice ...
     * @param isPrimaryCurrency ...
     * @returns Mixed size.
     */
    getPriceMixedSize(totalPrice, isPrimaryCurrency) {
        if (PriceUtility.isFamily(totalPrice) && !isPrimaryCurrency) {
            return false;
        }
        return true;
    }
}
__decorate([
    property({ type: Object })
], MiniPrice.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Object })
], MiniPrice.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], MiniPrice.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], MiniPrice.prototype, "settings", void 0);
__decorate([
    property({ type: Object, required: true })
], MiniPrice.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean })
], MiniPrice.prototype, "animatedPrice", void 0);
__decorate([
    property({ type: String })
], MiniPrice.prototype, "align", void 0);
__decorate([
    property({ type: Boolean })
], MiniPrice.prototype, "showEcoFee", void 0);
__decorate([
    property({ type: Boolean })
], MiniPrice.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], MiniPrice.prototype, "visibleModal", void 0);
//# sourceMappingURL=MiniPrice.js.map